import { useQuery } from '@apollo/client';
import { DocumentNode } from 'graphql';
import { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { useLocation } from 'react-router-dom';
function pickRowsAndCount(raw: any) {
  const rootKey = Object.keys(raw || {})[0]; // e.g. "getMaterials" | "getMaterialStats"
  const root = raw?.[rootKey];

  // ❶ Table endpoint: { data: [...], recordCount }
  if (root?.data !== undefined) {
    return { rows: root.data, count: root.recordCount ?? root.data?.length ?? 0 };
  }

  // ❷ Single-object endpoint: return one row array
  if (root !== undefined) {
    return { rows: [root], count: 1 };
  }

  // ❸ Nothing yet (loading or error)
  return { rows: undefined, count: undefined };
}
// This version expects a filtersReady boolean and always mounts the query, using skip accordingly
export function usePaginatedQueryV8(
  gqlQuery: DocumentNode,
  buildWhereFromFilterValues: (any) => string,
  initOrder: string,
  appStore: any,
  filtersReady: boolean,
  initFilterValues = {},
  historyReplace = true,
  stateSubName = undefined,
  persistedValues = {},
): any {
  const location = useLocation<any>();
  const history = useHistory();

  const state = stateSubName ? location.state?.[stateSubName] ?? null : location.state;

  const queryState = {
    pageIndex: state?.pageIndex ?? 0,
    filterValues: state?.filterValues ?? initFilterValues,
    order: state?.order ?? initOrder,
  };

  function newStateValues(values: { filterValues?: any; pageIndex?: number }): unknown {
    if (stateSubName) {
      return {
        ...location.state,
        [stateSubName]: {
          ...location.state?.[stateSubName],
          ...values,
        },
      };
    }
    return { ...location.state, ...values };
  }

  const tablePageSize = appStore.pageSize;
  const [initPageIndex, setInitPageIndex] = useState(queryState.pageIndex);

  const combinedFilters = { ...persistedValues, ...queryState.filterValues };

  const [queryParams, setQueryParams] = useState({
    offset: tablePageSize * initPageIndex,
    limit: tablePageSize,
    order: queryState.order,
    where: buildWhereFromFilterValues(combinedFilters),
  });

  const {
    loading,
    data: rawData,
    error,
    refetch,
  } = useQuery(gqlQuery, {
    variables: queryParams,
    fetchPolicy: 'network-only',
    skip: !filtersReady,
  });

  useEffect(() => {
    if (filtersReady) {
      refetch({ ...queryParams, order: initOrder });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initOrder]);

  const filtersChanged = useCallback(
    (userValues) => {
      const mergedValues = { ...userValues, ...persistedValues };
      const where = buildWhereFromFilterValues(mergedValues);
      const newQueryParams = { ...queryParams, offset: 0, where };
      setQueryParams(newQueryParams);
      if (filtersReady) refetch(newQueryParams);
      if (historyReplace)
        history.replace(
          location.pathname,
          newStateValues({ filterValues: userValues, pageIndex: 0 }),
        );
    },
    [queryParams, refetch, historyReplace, location.pathname, filtersReady],
  );

  const gridPageChanged = useCallback(
    (pageIndex, pageSize: number) => {
      setInitPageIndex(pageIndex);
      const newQueryParams = {
        ...queryParams,
        offset: pageSize * pageIndex,
        limit: pageSize,
      };
      setQueryParams(newQueryParams);
      if (filtersReady) refetch(newQueryParams);
      if (historyReplace) history.replace(location.pathname, newStateValues({ pageIndex }));
    },
    [historyReplace, location.pathname, queryParams, refetch, filtersReady],
  );
  const { rows, count } = pickRowsAndCount(rawData);
  return {
    loading,
    rawData,
    data: rows,
    dataCount: count,
    error,
    refetch,
    queryParams,
    setQueryParams,
    initPageIndex,
    setInitPageIndex,
    filtersChanged,
    gridPageChanged,
    queryState,
  };
}
